import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { GridColDef, GridPreProcessEditCellProps, GridSortCellParams, useGridApiRef } from '@mui/x-data-grid-pro';
import styled from 'styled-components';

import { Checkbox, FormControlLabel, Link, Stack, Typography } from '@mui/material';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SaveButton } from '@shared/components/SaveButton';
import { RelativeWrapper } from '@shared/styledComponents';
import { IOption } from '@shared/interfaces';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { statesList } from '@shared/constants/statesList';
import { NumberInput } from '@shared/inputs/NumberInput';
import { GridColNumber } from '@shared/components/grid/GridColNumber';
import { NumberInputWrapper } from '@styles/Admin-tools/DripFeederStyles';
import { LocalTabContent } from '@styles/CommonStyles';
import { colorTheme } from '@styles/muiTheme';

import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { IStateProductRestriction } from '@modules/admin/AdminTools/LoanRestrictionsConfiguration/interfaces';
import { IUpdateProductExclusionRulesPayload, TProductTypeExclusionRule } from '../../interfaces';
import { productTypes } from '../../shared/data';
import { ILenderUtilityTabProps } from '../LenderUtilityTabs';
import { PhoenixGridHeader } from '../../shared/PhoenixGridHeader';
import { calculationType, options } from '../DocFees/DocFeesTable/interfaces';
import { ProductRestrictionFormulaModal } from '../../../../AdminTools/LoanRestrictionsConfiguration/StateProductRestrictions/ProductRestrictionsFormulaModal';
import { requestUpdateProductRules, requestUpdateSubmitNakedDeal } from './queries';

const MiscRulesWrapper = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: ${colorTheme.palette.secondary.light};
  width: 30%;
  padding: 15px 10px;
  margin: 0px -8px;
`;

const allStateValues = statesList.map((state) => state.value);
const allStateSet = new Set(allStateValues);

export const getStateString = (statesList: IOption[]) => (statesList || []).map((state: IOption) => state.value).join(', ') || '';

export const ProductRules = ({ data, lenderId, mutate }: ILenderUtilityTabProps) => {
  const apiRef = useGridApiRef();
  const [productSubmission, setProductSubmission] = useState(data?.submitNakedDeal ?? false);

  const [updating, setUpdating] = useState(false);
  const [gapLtvLimit, setGapLtvLimit] = useState<number | undefined>(data?.generalInfo.gapLtvLimit);
  const [maxProduct, setMaxProduct] = useState<number | undefined>(data?.generalInfo.maxProduct);
  const [modalData, setModalData] = useState<IStateProductRestriction | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback((value: unknown, row: IStateProductRestriction) => {
    if (value === calculationType.Formula) {
      setModalData(row);
    }
  }, []);

  const preProcessAmount = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => {
    return {
      ...props,
      value: otherFieldsProps?.calculationType?.value !== calculationType.Formula ? props?.value : '',
    };
  };

  const preProcessFormula = ({ props, otherFieldsProps }: GridPreProcessEditCellProps) => ({
    ...props,
    value: otherFieldsProps?.calculationType.value !== calculationType.FlatFee ? props.value : null,
  });

  const formattedStatesList = [{ label: 'All', value: 'All' }, ...statesList];

  const columns: GridColDef[] = [
    dataGridCol('excludedStates', {
      flex: 1,
      textWrap: true,
      minWidth: 160,
      renderEditCell: (params) => (
        <GridColSelect
          isSearchable
          multiple
          limitTags={1}
          options={formattedStatesList}
          params={params}
          placeholderOpacity={1}
        />
      ),
      valueFormatter: (params) => {
        const states = params?.value || [];
        return getStateString(statesList.filter((state) => states.includes(state.value)));
      },
      sortComparator: (param1: GridSortCellParams, param2: GridSortCellParams) => {
        const value1 = getStateString(param1.value || []);
        const value2 = getStateString(param2.value || []);
        return value1.localeCompare(value2);
      },
      editable: true,
    }),
    dataGridCol('productType', {
      minWidth: 200,
      flex: 1,
      headerName: 'Product Type Name',
    }),
    dataGridCol('calculationType', {
      minWidth: 150,
      flex: 1,
      headerName: 'FlatFee/Formula',
      renderCell: (params) => {
        const value = params.value;
        return value ? calculationType[value as keyof typeof calculationType] : '';
      },
      renderEditCell: (params) => {
        return (
          <GridColSelect
            hideNoneOption
            isSearchable
            options={options}
            params={params}
            onChange={(value) => handleChange(value, params.row)}
          />
        );
      },
      editable: true,
    }),
    dataGridCol('maxAmount', {
      ...gridCurrencyCell,
      headerName: 'Max Amount',
      minWidth: 200,
      textWrap: true,
      preProcessEditCellProps: preProcessAmount,
      renderCell: (params) => {
        const isFormula = params.row.calculationType === calculationType.Formula;
        if (isFormula) return <Link onClick={() => setModalData(params.row)}>{params.row.maxAllowedFormula}</Link>;
        return (
          <GridColNumber
            params={params}
            prefix="$"
            decimalScale={2}
          />
        );
      },
      renderEditCell: (params) => {
        const isFormula = params.row.calculationType === calculationType.Formula;
        const formulaParams = {
          ...params,
          value: !isFormula ? params.row.maxAmount : params.row.maxAllowedFormula,
        };
        return (
          <>
            {!isFormula ? (
              <GridColNumber
                prefix="$"
                decimalScale={2}
                params={params}
              />
            ) : (
              <GridColTextInput
                disabled
                params={formulaParams}
              />
            )}
          </>
        );
      },
      flex: 1,
      editable: true,
    }),
    dataGridCol('maxAllowedFormula', {
      hide: true,
      editable: false,
      preProcessEditCellProps: preProcessFormula,
    }),
  ];

  const onSubmit = useCallback(async () => {
    const records = Array.from(apiRef.current.getRowModels().values());
    const updatedTypes = records.map((record) => {
      if (record.calculationType === calculationType.Formula) {
        return {
          ...record,
          isCurrent: true,
          isDeleted: false,
          excludedStates: record.excludedStates.includes('All') ? allStateValues : record.excludedStates,
        };
      }

      const isZeroAmount = !record.maxAmount || record.maxAmount === 0;

      return {
        ...record,
        calculationType: isZeroAmount ? calculationType.FlatFee : record.calculationType,
        isCurrent: true,
        isDeleted: false,
        excludedStates: record.excludedStates.includes('All') ? allStateValues : record.excludedStates,
      };
    });

    const requestPayload: IUpdateProductExclusionRulesPayload = {
      products: updatedTypes as TProductTypeExclusionRule[],
      gapLtvLimit,
      maxProduct,
    };

    setUpdating(true);
    await requestUpdateProductRules(lenderId, requestPayload);
    setUpdating(false);
  }, [requestUpdateProductRules, lenderId, gapLtvLimit, maxProduct]);

  const onChecked = useCallback(
    async (checked: boolean) => {
      setUpdating(true);
      setProductSubmission(checked);
      await requestUpdateSubmitNakedDeal(lenderId, checked);
      setUpdating(false);
    },
    [lenderId]
  );

  const getList = (list: IOption[], rules: TProductTypeExclusionRule[] = []) =>
    list?.map((item) => {
      const ruleMatch = rules.find((rule) => rule.productType === item.label);
      const excludedStates = ruleMatch?.excludedStates || [];

      const isAllSelected =
        excludedStates.length === allStateSet.size && excludedStates.every((excludedState) => allStateSet.has(excludedState));

      const resolvedCalculationType = ruleMatch
        ? calculationType[ruleMatch.calculationType] ?? (ruleMatch.maxAmount > 0 ? calculationType.FlatFee : null)
        : null;

      return {
        id: item.value,
        productType: item.label,
        excludedStates: isAllSelected ? ['All'] : excludedStates,
        maxAmount: ruleMatch?.maxAmount || 0,
        calculationType: resolvedCalculationType,
        maxAllowedFormula: ruleMatch?.maxAllowedFormula || '',
      };
    }) || [];

  const productRules = useMemo(() => {
    return getList(productTypes, data?.productTypeExclusionRules);
  }, [productTypes, data?.productTypeExclusionRules]);

  const gridActions = {
    onSave: async () => {
      //No action here, on save is handled by global save button
    },
  };

  return (
    <RelativeWrapper>
      <SaveButton
        disabled={updating}
        isLoading={updating}
        onClick={onSubmit}
      />
      <LocalTabContent>
        {!!modalData && (
          <ProductRestrictionFormulaModal
            apiRef={apiRef}
            modalData={modalData}
            setLoading={setLoading}
            loading={loading}
            lenderId={lenderId}
            lenderProductRulesMutate={mutate}
            handleClose={() => setModalData(null)}
          />
        )}
        <PhoenixGridHeader
          title="Product Rules"
          justify="flex-start"
        />
        <FormControlLabel
          label="Submit Naked"
          sx={{
            display: 'inline-flex',
            width: 'fit-content',
          }}
          control={
            <Checkbox
              checked={productSubmission}
              onChange={(_, checked) => onChecked(checked)}
            />
          }
        />
        <PhoenixGrid
          autoHeight
          withHeader
          canNotCopy
          disableSelectionOnClick
          noButton
          pagination={false}
          isRowSelectable={() => false}
          apiRef={apiRef}
          gridId={`Product Rules-${lenderId}`}
          columns={columns}
          loading={!data?.productTypeExclusionRules}
          rows={productRules}
          actions={gridActions}
        />
      </LocalTabContent>
      <MiscRulesWrapper>
        <Typography
          variant="subtitle1"
          mb={1}
        >
          Misc Product Rules
        </Typography>
        <Stack
          width="100%"
          display="flex"
          gap={3}
          flexDirection="row"
        >
          <NumberInputWrapper>
            <NumberInput
              label="Min GAP Ltv"
              suffix="%"
              decimalScale="2"
              value={gapLtvLimit}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setGapLtvLimit(parseFloat(event.target.value))}
            />
          </NumberInputWrapper>

          <NumberInputWrapper>
            <NumberInput
              label="Max BE Product"
              prefix="$"
              decimalScale="2"
              value={maxProduct}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setMaxProduct(parseFloat(event.target.value))}
            />
          </NumberInputWrapper>
        </Stack>
      </MiscRulesWrapper>
    </RelativeWrapper>
  );
};
